// One drawer for viewing / adding / editing a record of any table. Saves through /api/write (audited).
// Multi-user safe: shows who else has the record open, warns live if someone saves it meanwhile,
// and the server refuses a save made on an out-of-date copy (409).
import { useEffect, useState } from 'react';
import { ApiError, write } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/app';
import { useLive } from '../lib/live';
import { usePresence } from '../lib/presence';
import { Button, Drawer, Field, errText, useToast } from './kit';
import { splitList } from '../lib/format';
import { C3Busy } from './C3Loader';
import { OfflineNote, useConnection } from '../lib/connection';

export type FieldSpec = {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'multi' | 'list' | 'checkbox' | 'email' | 'url';
  options?: { value: string; label: string }[];
  required?: boolean;
  hint?: string;
  wide?: boolean;
};

type Rec = Record<string, unknown> & { id?: string; deleted_at?: string | null; updated_at?: string; updated_by?: string | null };

function toForm(f: FieldSpec, v: unknown): string | boolean {
  if (f.type === 'checkbox') return Boolean(v);
  if (f.type === 'list') return Array.isArray(v) ? v.join(', ') : '';
  if (f.type === 'multi') return Array.isArray(v) ? v.join(',') : '';
  if (v === null || v === undefined) return '';
  return String(v);
}

function fromForm(f: FieldSpec, v: string | boolean): unknown {
  if (f.type === 'checkbox') return Boolean(v);
  const s = String(v).trim();
  if (f.type === 'list') return splitList(s);
  if (f.type === 'multi') return s ? s.split(',').filter(Boolean) : [];
  if (s === '') return null;
  if (f.type === 'number') return Number(s);
  return s;
}

const emptyOf = (f: FieldSpec) => (f.type === 'list' || f.type === 'multi' ? [] : f.type === 'checkbox' ? false : null);

export function RecordDrawer({
  open,
  onClose,
  title,
  table,
  fields,
  record,
  defaults,
  canEdit,
  canDelete = canEdit,
  onSaved,
  label,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  table: string;
  fields: FieldSpec[];
  record: Rec | null;
  defaults?: Record<string, unknown>;
  canEdit: boolean;
  canDelete?: boolean;
  onSaved: (row: Rec) => void;
  label: (r: Rec) => string;
}) {
  const toast = useToast();
  const conn = useConnection();
  const { session } = useApp();
  const { setRecord, editing } = usePresence();
  const [base, setBase] = useState<Rec | null>(record);
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [stale, setStale] = useState<'remote' | 'conflict' | null>(null);
  const [dirty, setDirty] = useState(false);

  const fill = (src: Rec | null) => {
    const s: Rec = src ?? (defaults as Rec) ?? {};
    const next: Record<string, string | boolean> = {};
    for (const f of fields) next[f.key] = toForm(f, s[f.key]);
    setForm(next);
    setDirty(false);
  };

  useEffect(() => {
    if (!open) return;
    setBase(record);
    fill(record);
    setErr('');
    setStale(null);
    // defaults/fields are read when the drawer opens; re-running on every render would wipe typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record]);

  // tell others which record I have open
  const rid = base?.id;
  useEffect(() => {
    if (!open || !rid) return;
    setRecord(`${table}:${rid}`);
    return () => setRecord(null);
  }, [open, rid, table, setRecord]);

  // someone else saved this record while it's open
  useLive(
    [table],
    (c) => {
      const n = c.new as Rec;
      if (!n?.id || n.id !== rid) return;
      if (n.updated_by && n.updated_by === session?.user.id) return;
      if (n.updated_at && base?.updated_at && n.updated_at === base.updated_at) return;
      if (!dirty) {
        setBase(n);
        fill(n);
        toast({ tone: 'ok', text: 'This record was just updated by someone else — showing the latest.' });
      } else setStale('remote');
    },
    { filter: rid ? `id=eq.${rid}` : undefined, enabled: open && Boolean(rid) },
  );

  const isNew = !base?.id;
  const deleted = Boolean(base?.deleted_at);
  const othersHere = editing(table, rid);

  async function loadLatest() {
    if (!rid) return;
    const { data } = await supabase.from(table).select('*').eq('id', rid).maybeSingle();
    if (data) {
      setBase(data as Rec);
      fill(data as Rec);
      onSaved(data as Rec);
    }
    setStale(null);
    setErr('');
  }

  const set = (k: string, v: string | boolean) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  async function save() {
    setErr('');
    const values: Record<string, unknown> = { ...(isNew ? defaults : {}) };
    for (const f of fields) {
      const v = fromForm(f, form[f.key] ?? '');
      if (f.required && (v === null || (Array.isArray(v) && !v.length))) return setErr(`${f.label} is needed`);
      if (isNew || JSON.stringify(v) !== JSON.stringify(base?.[f.key] ?? emptyOf(f))) values[f.key] = v;
    }
    if (!isNew && Object.keys(values).length === 0) return onClose();
    if (!isNew && base?.updated_at) values._expect_updated_at = base.updated_at;
    setBusy(true);
    try {
      const row = await write<Rec>(table, isNew ? 'insert' : 'update', {
        id: base?.id,
        values,
        summary: `${isNew ? 'Added' : 'Edited'} ${label({ ...base, ...values } as Rec)}`,
      });
      toast({ tone: 'ok', text: isNew ? 'Added' : 'Saved' });
      onSaved(row);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setStale('conflict');
      else setErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(op: 'delete' | 'restore') {
    if (!base?.id) return;
    if (op === 'delete' && !window.confirm(`Delete "${label(base)}"? It can be restored later.`)) return;
    setBusy(true);
    try {
      const row = await write<Rec>(table, op, { id: base.id, summary: `${op === 'delete' ? 'Deleted' : 'Restored'} ${label(base)}` });
      toast({ tone: 'ok', text: op === 'delete' ? 'Deleted — it can be restored from the list or the audit log' : 'Restored' });
      onSaved(row);
      onClose();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const ro = !canEdit || deleted;

  return (
    <Drawer
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          {!isNew && canDelete ? (
            deleted ? (
              <Button onClick={() => void remove('restore')} disabled={busy}>
                Restore
              </Button>
            ) : (
              <Button kind="danger" onClick={() => void remove('delete')} disabled={busy}>
                Delete
              </Button>
            )
          ) : null}
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>{ro ? 'Close' : 'Cancel'}</Button>
          {!ro ? (
            <>
              <OfflineNote />
              <Button kind="primary" onClick={() => void save()} disabled={busy || stale !== null || conn.status === 'offline'}>
                {busy ? <C3Busy text="Saving…" accent /> : isNew ? 'Add' : 'Save'}
              </Button>
            </>
          ) : null}
        </>
      }
    >
      <div className="form">
        {othersHere.length ? (
          <div className="banner banner-info" role="status">
            {othersHere.map((o) => o.name).join(', ')} {othersHere.length === 1 ? 'has' : 'have'} this open too. If you both save, the second save is
            stopped so nothing is overwritten.
          </div>
        ) : null}
        {stale ? (
          <div className="banner banner-warn" role="alert">
            <span>
              {stale === 'conflict'
                ? 'Not saved — someone else changed this record after you opened it.'
                : 'Someone else just saved this record. Your typing is kept, but saving is paused.'}
            </span>
            <Button onClick={() => void loadLatest()}>Load their version</Button>
          </div>
        ) : null}
        {deleted ? <div className="error-box">This record is deleted. Restore it to edit.</div> : null}
        {fields.map((f) => (
          <Field key={f.key} label={f.label + (f.required ? ' *' : '')} hint={f.hint} group={f.type === 'multi'}>
            {f.type === 'textarea' ? (
              <textarea value={String(form[f.key] ?? '')} disabled={ro} onChange={(e) => set(f.key, e.target.value)} />
            ) : f.type === 'select' ? (
              <select value={String(form[f.key] ?? '')} disabled={ro} onChange={(e) => set(f.key, e.target.value)}>
                <option value="">—</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === 'multi' ? (
              <span className="multi">
                {f.options?.map((o) => {
                  const cur = String(form[f.key] ?? '').split(',').filter(Boolean);
                  const on = cur.includes(o.value);
                  return (
                    <button
                      type="button"
                      key={o.value}
                      className={`chip ${on ? 'on' : ''}`}
                      aria-pressed={on}
                      disabled={ro}
                      onClick={(e) => {
                        e.preventDefault();
                        set(f.key, (on ? cur.filter((x) => x !== o.value) : [...cur, o.value]).join(','));
                      }}
                    >
                      {on ? '✓ ' : ''}
                      {o.label}
                    </button>
                  );
                })}
              </span>
            ) : f.type === 'checkbox' ? (
              <span className="check">
                <input type="checkbox" checked={Boolean(form[f.key])} disabled={ro} onChange={(e) => set(f.key, e.target.checked)} />
              </span>
            ) : (
              <input
                type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : f.type === 'url' ? 'url' : 'text'}
                step={f.type === 'number' ? 'any' : undefined}
                value={String(form[f.key] ?? '')}
                disabled={ro}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </Field>
        ))}
        {err ? <div className="error-box">{err}</div> : null}
      </div>
    </Drawer>
  );
}
